import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, Scale, Activity, Heart, Droplet, Ruler, TrendingUp, TrendingDown, 
  Minus, Calendar, Edit, Trash2, LineChart, Target, Percent, Calculator,
  Wind, Dumbbell, Timer
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Metric configuration
const METRICS = {
  weight: { label: 'Gewicht', unit: 'kg', icon: Scale, color: '#3B82F6', decimals: 1 },
  bmi: { label: 'BMI', unit: '', icon: Calculator, color: '#8B5CF6', decimals: 1 },
  body_fat_percent: { label: 'Körperfett', unit: '%', icon: Percent, color: '#F59E0B', decimals: 1 },
  muscle_mass: { label: 'Muskelmasse', unit: 'kg', icon: Dumbbell, color: '#10B981', decimals: 1 },
  water_percent: { label: 'Wasseranteil', unit: '%', icon: Droplet, color: '#06B6D4', decimals: 1 },
  visceral_fat: { label: 'Viszeralfett', unit: '', icon: Target, color: '#7C3AED', decimals: 0 },
  metabolic_age: { label: 'Stoffwechselalter', unit: 'Jahre', icon: Timer, color: '#D97706', decimals: 0 },
  waist: { label: 'Bauchumfang', unit: 'cm', icon: Ruler, color: '#EC4899', decimals: 1 },
  hip: { label: 'Hüftumfang', unit: 'cm', icon: Ruler, color: '#F472B6', decimals: 1 },
  chest: { label: 'Brust', unit: 'cm', icon: Ruler, color: '#EC4899', decimals: 1 },
  arm_left: { label: 'Linker Arm', unit: 'cm', icon: Ruler, color: '#A855F7', decimals: 1 },
  arm_right: { label: 'Rechter Arm', unit: 'cm', icon: Ruler, color: '#A855F7', decimals: 1 },
  thigh_left: { label: 'Linker Oberschenkel', unit: 'cm', icon: Ruler, color: '#6366F1', decimals: 1 },
  thigh_right: { label: 'Rechter Oberschenkel', unit: 'cm', icon: Ruler, color: '#6366F1', decimals: 1 },
  neck: { label: 'Hals', unit: 'cm', icon: Ruler, color: '#EAB308', decimals: 1 },
  blood_pressure_systolic: { label: 'Blutdruck (sys)', unit: 'mmHg', icon: Heart, color: '#EF4444', decimals: 0 },
  blood_pressure_diastolic: { label: 'Blutdruck (dia)', unit: 'mmHg', icon: Heart, color: '#F87171', decimals: 0 },
  heart_rate: { label: 'Ruhepuls', unit: 'bpm', icon: Activity, color: '#DC2626', decimals: 0 },
  blood_oxygen: { label: 'Blutsauerstoff', unit: '%', icon: Wind, color: '#0EA5E9', decimals: 1 },
};

const CustomerBodyTracking = ({ customerId, customerName, onDataChange, isPortal = false }) => {
  const [entries, setEntries] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('weight');
  const [chartDays, setChartDays] = useState(90);

  // Wunschgewicht / Ziel-Linie (nur Admin-Ansicht, Recht can_edit_customers)
  const { hasPermission } = useAuth();
  const canEditGoal = !isPortal && (hasPermission ? hasPermission('can_edit_customers') : false);
  const [goalWeight, setGoalWeight] = useState(null);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    weight: '',
    height: '',
    body_fat_percent: '',
    muscle_mass: '',
    water_percent: '',
    visceral_fat: '',
    metabolic_age: '',
    waist: '',
    hip: '',
    chest: '',
    arm_left: '',
    arm_right: '',
    thigh_left: '',
    thigh_right: '',
    neck: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    heart_rate: '',
    blood_oxygen: '',
    notes: ''
  });

  // Get the correct token based on context
  const getToken = () => {
    return isPortal 
      ? localStorage.getItem('portal_token') 
      : localStorage.getItem('token');
  };

  // Get the correct API endpoint based on context
  const getApiUrl = (endpoint) => {
    if (isPortal) {
      return `${API}/portal/nutrition/body-tracking${endpoint}`;
    }
    return `${API}/body-tracking/customer/${customerId}${endpoint}`;
  };

  useEffect(() => {
    if (customerId || isPortal) {
      fetchEntries();
      fetchProgress();
    }
  }, [customerId, isPortal]);

  useEffect(() => {
    if (!isPortal && customerId) fetchGoal();
  }, [customerId, isPortal]);

  const fetchGoal = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/body-tracking/customer/${customerId}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const w = res.data?.body_goals?.weight;
      setGoalWeight(typeof w === 'number' ? w : null);
    } catch (error) {
      // Ziel ist optional
    }
  };

  const saveGoal = async (clear = false) => {
    try {
      const token = getToken();
      let val = null;
      if (!clear) {
        val = parseFloat(String(goalInput).replace(',', '.'));
        if (isNaN(val)) { toast.error('Bitte ein gültiges Gewicht eingeben'); return; }
      }
      const res = await axios.put(
        `${API}/body-tracking/customer/${customerId}/goals`,
        { weight: clear ? null : val },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const w = res.data?.body_goals?.weight;
      setGoalWeight(typeof w === 'number' ? w : null);
      setGoalDialogOpen(false);
      toast.success(clear ? 'Wunschgewicht entfernt' : 'Wunschgewicht gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern des Ziels');
    }
  };

  const fetchEntries = async () => {
    try {
      const token = getToken();
      const url = isPortal 
        ? `${API}/portal/nutrition/body-tracking`
        : `${API}/body-tracking/customer/${customerId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching body tracking:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const token = getToken();
      const url = isPortal 
        ? `${API}/portal/nutrition/body-tracking/progress`
        : `${API}/body-tracking/customer/${customerId}/progress`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openNewEntry = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      weight: '',
      height: entries.length > 0 && entries[0].height ? entries[0].height.toString() : '',
      body_fat_percent: '',
      muscle_mass: '',
      water_percent: '',
      visceral_fat: '',
      metabolic_age: '',
      waist: '',
      hip: '',
      chest: '',
      arm_left: '',
      arm_right: '',
      thigh_left: '',
      thigh_right: '',
      neck: '',
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      heart_rate: '',
      blood_oxygen: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      weight: entry.weight?.toString() || '',
      height: entry.height?.toString() || '',
      body_fat_percent: entry.body_fat_percent?.toString() || '',
      muscle_mass: entry.muscle_mass?.toString() || '',
      water_percent: entry.water_percent?.toString() || '',
      visceral_fat: entry.visceral_fat?.toString() || '',
      metabolic_age: entry.metabolic_age?.toString() || '',
      waist: entry.waist?.toString() || '',
      hip: (entry.hip ?? entry.hips)?.toString() || '',
      chest: entry.chest?.toString() || '',
      arm_left: (entry.arm_left ?? entry.arm)?.toString() || '',
      arm_right: (entry.arm_right ?? entry.arm)?.toString() || '',
      thigh_left: (entry.thigh_left ?? entry.thigh)?.toString() || '',
      thigh_right: (entry.thigh_right ?? entry.thigh)?.toString() || '',
      neck: entry.neck?.toString() || '',
      blood_pressure_systolic: entry.blood_pressure_systolic?.toString() || '',
      blood_pressure_diastolic: entry.blood_pressure_diastolic?.toString() || '',
      heart_rate: entry.heart_rate?.toString() || '',
      blood_oxygen: entry.blood_oxygen?.toString() || '',
      notes: entry.notes || ''
    });
    setDialogOpen(true);
  };

  const saveEntry = async () => {
    try {
      const token = getToken();
      
      // Prepare data - convert strings to numbers where needed
      const parseArm = (l, r) => {
        const ln = l ? parseFloat(l) : null;
        const rn = r ? parseFloat(r) : null;
        const both = [ln, rn].filter((v) => v !== null);
        return both.length > 0 ? Number((both.reduce((a, b) => a + b, 0) / both.length).toFixed(1)) : null;
      };
      const data = {
        customer_id: customerId,
        date: new Date(formData.date).toISOString(),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        body_fat_percent: formData.body_fat_percent ? parseFloat(formData.body_fat_percent) : null,
        muscle_mass: formData.muscle_mass ? parseFloat(formData.muscle_mass) : null,
        water_percent: formData.water_percent ? parseFloat(formData.water_percent) : null,
        visceral_fat: formData.visceral_fat ? parseFloat(formData.visceral_fat) : null,
        metabolic_age: formData.metabolic_age ? parseInt(formData.metabolic_age) : null,
        waist: formData.waist ? parseFloat(formData.waist) : null,
        hip: formData.hip ? parseFloat(formData.hip) : null,
        chest: formData.chest ? parseFloat(formData.chest) : null,
        arm_left: formData.arm_left ? parseFloat(formData.arm_left) : null,
        arm_right: formData.arm_right ? parseFloat(formData.arm_right) : null,
        // Legacy `arm` = Durchschnitt damit alte Charts/Reports
        // weiterhin Werte liefern.
        arm: parseArm(formData.arm_left, formData.arm_right),
        thigh_left: formData.thigh_left ? parseFloat(formData.thigh_left) : null,
        thigh_right: formData.thigh_right ? parseFloat(formData.thigh_right) : null,
        thigh: parseArm(formData.thigh_left, formData.thigh_right),
        neck: formData.neck ? parseFloat(formData.neck) : null,
        blood_pressure_systolic: formData.blood_pressure_systolic ? parseInt(formData.blood_pressure_systolic) : null,
        blood_pressure_diastolic: formData.blood_pressure_diastolic ? parseInt(formData.blood_pressure_diastolic) : null,
        heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : null,
        blood_oxygen: formData.blood_oxygen ? parseFloat(formData.blood_oxygen) : null,
        notes: formData.notes || null
      };
      
      if (editingEntry) {
        const url = isPortal 
          ? `${API}/portal/nutrition/body-tracking/${editingEntry.id}`
          : `${API}/body-tracking/${editingEntry.id}`;
        await axios.put(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Eintrag aktualisiert');
      } else {
        const url = isPortal 
          ? `${API}/portal/nutrition/body-tracking`
          : `${API}/body-tracking`;
        await axios.post(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Eintrag gespeichert');
      }
      
      setDialogOpen(false);
      fetchEntries();
      fetchProgress();
      // Notify parent about data change
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    
    try {
      const token = getToken();
      const url = isPortal 
        ? `${API}/portal/nutrition/body-tracking/${entryId}`
        : `${API}/body-tracking/${entryId}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eintrag gelöscht');
      fetchEntries();
      fetchProgress();
      // Notify parent about data change
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    const filtered = entries
      .filter(e => e[selectedMetric] != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - chartDays);
    
    return filtered
      .filter(e => new Date(e.date) >= cutoffDate)
      .map(e => ({
        date: new Date(e.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' }),
        fullDate: new Date(e.date).toLocaleDateString('de-CH'),
        value: e[selectedMetric]
      }));
  }, [entries, selectedMetric, chartDays]);

  // Get latest values for quick display
  const latestEntry = entries.length > 0 ? entries[0] : null;

  const renderProgressCard = (metricKey) => {
    if (!progress?.has_data || !progress[metricKey]) return null;
    
    const data = progress[metricKey];
    const metric = METRICS[metricKey];
    const Icon = metric.icon;
    const isPositive = data.change > 0;
    const isNegativeGood = ['body_fat_percent', 'waist', 'hip', 'chest', 'neck'].includes(metricKey);
    const isGood = isNegativeGood ? !isPositive : isPositive;
    
    return (
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4" style={{ color: metric.color }} />
          <span className="text-sm font-medium">{metric.label}</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <span className="text-2xl font-bold">{data.latest}</span>
            <span className="text-sm text-gray-500 ml-1">{metric.unit}</span>
          </div>
          <div className={`flex items-center gap-1 text-sm ${data.change === 0 ? 'text-gray-500' : isGood ? 'text-green-600' : 'text-red-600'}`}>
            {data.change === 0 ? (
              <Minus className="h-4 w-4" />
            ) : isPositive ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
            <span>{data.change > 0 ? '+' : ''}{data.change} {metric.unit}</span>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Start: {data.first} {metric.unit}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold">Körper-Tracking</h3>
          <Badge variant="outline">{entries.length} Einträge</Badge>
        </div>
        <Button size="sm" onClick={openNewEntry}>
          <Plus className="h-4 w-4 mr-2" />
          Neue Messung
        </Button>
      </div>

      {entries.length === 0 ? (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-8 text-center text-gray-500">
            <Scale className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Körperdaten vorhanden.</p>
            <p className="text-sm mt-2">Erfassen Sie die erste Messung, um den Fortschritt zu verfolgen.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="chart" className="space-y-4">
          <TabsList>
            <TabsTrigger value="chart">📈 Verlauf</TabsTrigger>
            <TabsTrigger value="progress">🎯 Fortschritt</TabsTrigger>
            <TabsTrigger value="history">📋 Historie</TabsTrigger>
          </TabsList>

          {/* Chart Tab */}
          <TabsContent value="chart" className="space-y-4">
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Verlauf</CardTitle>
                  <div className="flex items-center gap-2">
                    {canEditGoal && selectedMetric === 'weight' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setGoalInput(goalWeight != null ? String(goalWeight) : ''); setGoalDialogOpen(true); }}
                        data-testid="body-goal-edit-btn"
                      >
                        <Target className="w-4 h-4 mr-1" />
                        {goalWeight != null ? `Ziel: ${goalWeight} kg` : 'Ziel setzen'}
                      </Button>
                    )}
                    <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(METRICS).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            {config.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={chartDays.toString()} onValueChange={(v) => setChartDays(parseInt(v))}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 Tage</SelectItem>
                        <SelectItem value="90">90 Tage</SelectItem>
                        <SelectItem value="180">6 Monate</SelectItem>
                        <SelectItem value="365">1 Jahr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={METRICS[selectedMetric].color} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={METRICS[selectedMetric].color} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                          domain={['auto', 'auto']}
                        />
                        <Tooltip 
                          formatter={(value) => [
                            `${value} ${METRICS[selectedMetric].unit}`, 
                            METRICS[selectedMetric].label
                          ]}
                          labelFormatter={(label) => `Datum: ${label}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke={METRICS[selectedMetric].color}
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorValue)"
                        />
                        {selectedMetric === 'weight' && goalWeight != null && (
                          <ReferenceLine
                            y={goalWeight}
                            stroke="#C9A227"
                            strokeDasharray="6 4"
                            strokeWidth={1.5}
                            label={{ value: `Ziel: ${goalWeight} kg`, position: 'insideTopRight', fill: '#C9A227', fontSize: 11 }}
                          />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-500">
                    Keine Daten für {METRICS[selectedMetric].label} vorhanden
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ziel-Gewicht Dialog */}
            <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
              <DialogContent data-testid="body-goal-dialog">
                <DialogHeader>
                  <DialogTitle>Wunschgewicht</DialogTitle>
                  <DialogDescription>Lege das Zielgewicht dieses Kunden fest. Es erscheint als gestrichelte Linie in der Verlaufskurve.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  <Label htmlFor="goal-weight-input">Zielgewicht (kg)</Label>
                  <Input
                    id="goal-weight-input"
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    placeholder="z.B. 75"
                    data-testid="body-goal-input"
                  />
                </div>
                <DialogFooter className="flex justify-between sm:justify-between">
                  {goalWeight != null && (
                    <Button variant="ghost" className="text-red-600" onClick={() => saveGoal(true)} data-testid="body-goal-clear-btn">
                      Löschen
                    </Button>
                  )}
                  <div className="flex gap-2 ml-auto">
                    <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>Abbrechen</Button>
                    <Button onClick={() => saveGoal(false)} data-testid="body-goal-save-btn">Speichern</Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {latestEntry && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {latestEntry.weight && (
                  <Card className="p-3 bg-white border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Scale className="h-4 w-4" />
                      <span>Gewicht</span>
                    </div>
                    <div className="text-xl font-bold mt-1">{latestEntry.weight} kg</div>
                  </Card>
                )}
                {latestEntry.bmi && (
                  <Card className="p-3 bg-white border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Calculator className="h-4 w-4" />
                      <span>BMI</span>
                    </div>
                    <div className="text-xl font-bold mt-1">{latestEntry.bmi}</div>
                  </Card>
                )}
                {latestEntry.body_fat_percent && (
                  <Card className="p-3 bg-white border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Percent className="h-4 w-4" />
                      <span>Körperfett</span>
                    </div>
                    <div className="text-xl font-bold mt-1">{latestEntry.body_fat_percent}%</div>
                  </Card>
                )}
                {(latestEntry.blood_pressure_systolic && latestEntry.blood_pressure_diastolic) && (
                  <Card className="p-3 bg-white border-gray-200">
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Heart className="h-4 w-4" />
                      <span>Blutdruck</span>
                    </div>
                    <div className="text-xl font-bold mt-1">
                      {latestEntry.blood_pressure_systolic}/{latestEntry.blood_pressure_diastolic}
                    </div>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-4">
            {progress?.has_data ? (
              <>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Tracking seit</p>
                      <p className="font-semibold text-blue-900">
                        {new Date(progress.first_date).toLocaleDateString('de-CH')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-600">Letzte Messung</p>
                      <p className="font-semibold text-blue-900">
                        {new Date(progress.latest_date).toLocaleDateString('de-CH')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-600">Messungen</p>
                      <p className="font-semibold text-blue-900">{progress.total_entries}</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {renderProgressCard('weight')}
                  {renderProgressCard('bmi')}
                  {renderProgressCard('body_fat_percent')}
                  {renderProgressCard('muscle_mass')}
                  {renderProgressCard('waist')}
                  {renderProgressCard('hip')}
                  {renderProgressCard('chest')}
                  {renderProgressCard('arm_left')}
                  {renderProgressCard('arm_right')}
                  {renderProgressCard('thigh_left')}
                  {renderProgressCard('thigh_right')}
                </div>
              </>
            ) : (
              <Card className="bg-white border-gray-200">
                <CardContent className="p-8 text-center text-gray-500">
                  <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Mindestens 2 Einträge erforderlich für Fortschrittsanzeige.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-2">
            {entries.map((entry) => (
              <Card key={entry.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 p-2 rounded-full">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {new Date(entry.date).toLocaleDateString('de-CH', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                          {' um '}
                          {new Date(entry.date).toLocaleTimeString('de-CH', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                          {entry.weight && <span>⚖️ {entry.weight} kg</span>}
                          {entry.bmi && <span>📊 BMI {entry.bmi}</span>}
                          {entry.body_fat_percent && <span>💧 {entry.body_fat_percent}% Fett</span>}
                          {entry.muscle_mass && <span>💪 {entry.muscle_mass} kg Muskeln</span>}
                          {entry.visceral_fat && <span>🎯 Viszeralfett {entry.visceral_fat}</span>}
                          {entry.metabolic_age && <span>🔥 Stoffwechsel {entry.metabolic_age} J.</span>}
                          {(entry.blood_pressure_systolic && entry.blood_pressure_diastolic) && (
                            <span>❤️ {entry.blood_pressure_systolic}/{entry.blood_pressure_diastolic}</span>
                          )}
                          {entry.blood_oxygen && <span>🌬️ SpO2 {entry.blood_oxygen}%</span>}
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-1 italic">"{entry.notes}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditEntry(entry)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteEntry(entry.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      )}

      {/* Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Messung bearbeiten' : 'Neue Messung erfassen'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Date */}
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
              />
            </div>

            {/* Body Measurements */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Körpermasse
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Gewicht (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    placeholder="75.5"
                  />
                </div>
                <div>
                  <Label>Grösse (cm)</Label>
                  <Input
                    type="number"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    placeholder="175"
                  />
                </div>
                <div>
                  <Label>Körperfett (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.body_fat_percent}
                    onChange={(e) => handleInputChange('body_fat_percent', e.target.value)}
                    placeholder="22.5"
                  />
                </div>
                <div>
                  <Label>Muskelmasse (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.muscle_mass}
                    onChange={(e) => handleInputChange('muscle_mass', e.target.value)}
                    placeholder="35.0"
                  />
                </div>
                <div>
                  <Label>Wasseranteil (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.water_percent}
                    onChange={(e) => handleInputChange('water_percent', e.target.value)}
                    placeholder="55.0"
                  />
                </div>
                <div>
                  <Label>Viszeralfett (Index)</Label>
                  <Input
                    type="number"
                    step="1"
                    value={formData.visceral_fat}
                    onChange={(e) => handleInputChange('visceral_fat', e.target.value)}
                    placeholder="8"
                  />
                  <p className="text-xs text-gray-500 mt-1">Normal: 1-12</p>
                </div>
                <div>
                  <Label>Stoffwechselalter</Label>
                  <Input
                    type="number"
                    step="1"
                    value={formData.metabolic_age}
                    onChange={(e) => handleInputChange('metabolic_age', e.target.value)}
                    placeholder="35"
                  />
                  <p className="text-xs text-gray-500 mt-1">Jahre</p>
                </div>
              </div>
            </div>

            {/* Body Circumference */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Ruler className="h-4 w-4" />
                Umfänge
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Bauchumfang (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.waist}
                    onChange={(e) => handleInputChange('waist', e.target.value)}
                    placeholder="85"
                  />
                </div>
                <div>
                  <Label>Hüftumfang (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.hip}
                    onChange={(e) => handleInputChange('hip', e.target.value)}
                    placeholder="100"
                  />
                </div>
                <div>
                  <Label>Brustumfang (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.chest}
                    onChange={(e) => handleInputChange('chest', e.target.value)}
                    placeholder="95"
                  />
                </div>
                <div>
                  <Label>Linker Arm (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.arm_left}
                    onChange={(e) => handleInputChange('arm_left', e.target.value)}
                    placeholder="33"
                  />
                </div>
                <div>
                  <Label>Rechter Arm (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.arm_right}
                    onChange={(e) => handleInputChange('arm_right', e.target.value)}
                    placeholder="33"
                  />
                </div>
                <div>
                  <Label>Linker Oberschenkel (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.thigh_left}
                    onChange={(e) => handleInputChange('thigh_left', e.target.value)}
                    placeholder="58"
                  />
                </div>
                <div>
                  <Label>Rechter Oberschenkel (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.thigh_right}
                    onChange={(e) => handleInputChange('thigh_right', e.target.value)}
                    placeholder="58"
                  />
                </div>
                <div>
                  <Label>Hals (cm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.neck}
                    onChange={(e) => handleInputChange('neck', e.target.value)}
                    placeholder="40"
                  />
                </div>
              </div>
            </div>

            {/* Vitals */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Vitalwerte
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Blutdruck systolisch</Label>
                  <Input
                    type="number"
                    value={formData.blood_pressure_systolic}
                    onChange={(e) => handleInputChange('blood_pressure_systolic', e.target.value)}
                    placeholder="120"
                  />
                </div>
                <div>
                  <Label>Blutdruck diastolisch</Label>
                  <Input
                    type="number"
                    value={formData.blood_pressure_diastolic}
                    onChange={(e) => handleInputChange('blood_pressure_diastolic', e.target.value)}
                    placeholder="80"
                  />
                </div>
                <div>
                  <Label>Ruhepuls (bpm)</Label>
                  <Input
                    type="number"
                    value={formData.heart_rate}
                    onChange={(e) => handleInputChange('heart_rate', e.target.value)}
                    placeholder="70"
                  />
                </div>
                <div>
                  <Label>Blutsauerstoff (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.blood_oxygen}
                    onChange={(e) => handleInputChange('blood_oxygen', e.target.value)}
                    placeholder="98"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notizen</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Zusätzliche Anmerkungen..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveEntry}>
              {editingEntry ? 'Aktualisieren' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default React.memo(CustomerBodyTracking);
